import axios from 'axios';
import { Tender, TenderAnswer, TenderFeePeriodReport, Tendering, TenderingPart, TenderManagerTool } from '../models/tendering';
import { deepCloneObject } from '../utils/clone';
import apiClient from './api';
import * as moduleService from './moduleService';

export const getTenderingDocument = async (id: number, properties: string[]) : Promise<Tendering> => {
    const response = await apiClient.post(`/tenderingDocument/${id}`, properties);
    
    return response.data;
}

export const getTenderingDocuments = async () : Promise<Tendering[]> => {
    const response = await apiClient.get(`/tenderingDocument`);
    
    return response.data;
}

export const getTenderDocumentsByTenderingID = async (id: number, properties: string[]) : Promise<Tender[]> => {
    const response = await apiClient.post(`/tenderDocument/tendering/${id}`, properties);
    
    return response.data;
}

export const getTenderDocument = async (id: number, properties?: string[]) : Promise<Tender> => {
    if(!properties) {
        properties = [];
    }

    const response = await apiClient.post(`/tenderDocument/tender/${id}`, properties);
    
    return response.data;
}

export const getTenderManagerDocuments = async () : Promise<TenderManagerTool[]> => {
  const response = await apiClient.get(`tenderdocument/tenderManager`);

  return response.data;
}

export const getTendersDocumentFilteredAnswers = async (id: number, properties: string[], questions: number[]) : Promise<Tender[]> => {
    let params = new URLSearchParams();
    
    params.append('tenderingID', id.toString());
    properties.forEach(id => {
      params.append('properties', id.toString());
    })
    questions.forEach(id => {
      params.append('questions', id.toString());
    })

    const response = await apiClient.get(`/tenderdocument/filteredanswers`, {params});
    
    return response.data;
}

export const getTendersDocumentFilteredAnswersByIDs = async (ids: number[], properties: string[], questions: number[]) : Promise<Tender[]> => {
    let params = new URLSearchParams();
    
    ids.forEach(id => {
      params.append('tenderIDs', id.toString());
    })
    properties.forEach(id => {
      params.append('properties', id.toString());
    })
    questions.forEach(id => {
      params.append('questions', id.toString());
    })

    const response = await apiClient.get(`/tenderDocument/ids/filteredanswers`, {params});
    
    return response.data;
}

export const updateTenderDocument = async (id: number, tender: Tender): Promise<boolean> => {
    const response = await apiClient.put(`/tenderDocument/${id}`, tender)
    
    return response.data;
}

export const updateTenderingDocument = async (id: number, tendering: Tendering): Promise<boolean> => {
    const response = await apiClient.put(`/tenderingDocument/${id}`, tendering)
    
    return response.data;
}

export const updateTenderingWithNewQuestion = async (id: number, question: any): Promise<boolean> => {
    const response = await apiClient.put(`/tenderingdocument/question/${id}`, question);
    return response.data
}

export const updateTenderingPartsByTenderingID = async (id: number, content: { tenderingParts: TenderingPart[] }): Promise<boolean> => {
    const response = await apiClient.put(`/tenderdocument/tendering/${id}`, content);
    return response.data
}

export const generateTenderingFromExcel = async (formData: FormData): Promise<number> => {
    const response = await apiClient.post(`/tenderingdocument/excel`, formData);
    return response.data;
}

export const getQuestionsFromExcel = async (formData: FormData): Promise<any[]> => {
    const response = await apiClient.post(`/tenderingdocument/questions/excel`, formData);
    return response.data;
}

export const updateTenderFeePeriodReport = async (report: any): Promise<boolean> => {
    const tenderReport: TenderFeePeriodReport = deepCloneObject(report);

    const tender = await getTenderDocument(tenderReport.tenderID);
    if (tender.reportPeriods) {
        let found;
        tender.reportPeriods.forEach((r, index) => {
          if (r.reportID === tenderReport.reportID) {
            found = index;
          }
        });
        if (found !== undefined) { tender.reportPeriods[found] = tenderReport; }
        }
        else {
          tender.reportPeriods = [tenderReport];
        }

    return apiClient.put(`/tenderdocument/${tender.tenderID}`, tender, {responseType: 'text'});
}

export const updateTenderManagerTool = async (tenderManagerTool: TenderManagerTool): Promise<boolean> => {
  const tenderManagerToolClone = deepCloneObject(tenderManagerTool);

  const response = await getTenderDocument(tenderManagerToolClone.tenderID).then(tender => {
    tender.tenderManagerTool = tenderManagerToolClone;
    return apiClient.put(`/tenderDocument/${tender.tenderID}`, tender, {responseType: 'text'});
  });
  
  return response.data;
}

export const updateTenderIndexing = async (tender: Tender): Promise<boolean> => {
  const indexing = deepCloneObject(tender.indexing);
  
  // Get current tender document
  const currentTender = await getTenderDocument(tender.tenderID);
  
  // Update the indexing property
  currentTender.indexing = indexing;
  
  // Update the tender document
  const response = await apiClient.put(`/tenderDocument/${currentTender.tenderID}`, currentTender);
  
  return response.data;
}

export const changeTenderHideStatus = async (tenderID: number): Promise<any> => {
  await changeModuleHiddenProperty(tenderID);
  
  const tender = await getTenderDocument(tenderID, ["content.tenderManagerTool"]);
  
  if (tender.tenderManagerTool) {
    const tenderManagerTool = deepCloneObject(tender.tenderManagerTool);
    if (tender.tenderProperties.hidden) {
      tenderManagerTool.statuses = tenderManagerTool.statuses.filter((elem: number) => elem !== 1);
    } else {
      tenderManagerTool.statuses.push(1);
    }
    tender.tenderManagerTool = tenderManagerTool;
  }
  
  tender.tenderProperties.hidden = !tender.tenderProperties.hidden;
  
  const response = await apiClient.put(`/tenderdocument/${tenderID}`, tender, {responseType: 'text'});
  return response.data;
}

export const changeTenderBlockedStatus = async (tenderID: number, tagID: number): Promise<any> => {
  await changeModuleBlockProperty(tenderID);
  
  const tender = await getTenderDocument(tenderID, ["content.tenderManagerTool"]);
  
  if (tender.tenderManagerTool) {
    const tenderManagerTool = deepCloneObject(tender.tenderManagerTool);
    if (tenderManagerTool.statuses.includes(tagID)) {
      tenderManagerTool.statuses = tenderManagerTool.statuses.filter((elem: number) => elem !== tagID);
    } else {
      tenderManagerTool.statuses.push(tagID);
    }
    tender.tenderManagerTool = tenderManagerTool;
  }
  
  const response = await apiClient.put(`/tenderdocument/${tender.tenderID}`, tender, {responseType: 'text'});
  return response.data;
}

export const changeModuleBlockProperty = async (tenderID: number): Promise<any> => {
  const module = await moduleService.getModule(tenderID);
  module.isBlocked = !module.isBlocked;
  return moduleService.updateModule(tenderID, module);
}

export const changeModuleHiddenProperty = async (tenderID: number): Promise<any> => {
  const module = await moduleService.getModule(tenderID);
  module.isHidden = !module.isHidden;
  return moduleService.updateModule(tenderID, module);
}

export const updateTenderAnswer = async (
  tenderID: number, 
  newAnswer: TenderAnswer, 
  userID: number, 
  updateTenderChanges?: (tender: Tender, org: TenderAnswer, newAnswer: TenderAnswer, userID: number, changeReqByUserID?: number) => void | Promise<void>,
  changeReqByUserID?: number
): Promise<boolean> => {
  const tender = await getTenderDocument(tenderID);
  
  const curr = tender.tenderAnswers?.find(old => old.questionID === newAnswer.questionID);
  let org: TenderAnswer;
  
  if (curr) {
    org = deepCloneObject(curr);
    let found;
    tender.tenderAnswers?.forEach((element, index) => {
      if (element.questionID === newAnswer.questionID) {
        found = index;
      }
    });
    if (found !== undefined && tender.tenderAnswers) {
      tender.tenderAnswers[found] = newAnswer;
    }
  } else {
    delete newAnswer.migrationID;
    if (!tender.tenderAnswers) {
      tender.tenderAnswers = [];
    }
    tender.tenderAnswers.push(newAnswer);
    tender.tenderAnswers.sort((a, b) => a.questionID - b.questionID);
    org = deepCloneObject(newAnswer);
    org.value = null;
  }

  if (updateTenderChanges) {
    await updateTenderChanges(tender, org, newAnswer, userID, changeReqByUserID);
  }

  const response = await apiClient.put(`/tenderdocument/${tenderID}`, tender, { responseType: 'text' });
  return response.data;
}

